import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Loader from "./Loader";

const ExploreCourses = () => {
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/courses/get-all`
        );
        setCourses(res.data.data || []);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching courses:", error);
        setError(error.message);
      }
    };
    fetchCourses();
  }, []);

  const slugify = (text) =>
    text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^\w\-]+/g, "")
      .replace(/\-\-+/g, "-");

  const handleCardClick = (courseId, courseCategory) => {
    navigate(`/courses/${slugify(courseCategory)}`, {
      state: { courseId: courseId },
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <section className="px-6 py-10 bg-gray-50 min-h-screen">
      {/* Header Section */}
      <div className="text-center mb-12">
        <h1 className="text-4xl lg:text-5xl font-bold text-primaryColor mb-3 heading">
          Explore Our Courses 🎓
        </h1>
        <p className="text-gray-600 text-lg sub-heading">
          Discover a variety of courses designed to help you gain in-demand
          skills and advance your career. Explore our expertly crafted programs,
          choose what fits your goals, and start learning today.
        </p>
      </div>

      {/* Courses Grid */}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          <Loader />
        ) : error ? (
          <div className="col-span-full text-center py-16">
            <div className="text-6xl mb-4">⚠️</div>
            <h3 className="text-2xl font-bold text-primaryTextColor mb-2">
              Error Loading Images
            </h3>
            <p className="text-secondaryTextColor">{error}</p>
          </div>
        ) : (
          courses.map((course) => (
            <div
              key={course._id}
              onClick={() => handleCardClick(course._id, course.category)}
              className="bg-gray-100 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group relative overflow-hidden"
            >
              <div className="p-6">
                {/* Category Badge */}
                <span className="inline-block bg-secondaryColor text-white text-xs font-semibold px-3 py-1 rounded-full mb-3 uppercase heading">
                  {course.category}
                </span>

                {/* Course Name */}
                <h3 className="text-xl font-bold text-primaryTextColor mb-2 group-hover:text-primaryColor transition-colors heading">
                  {course.name}
                </h3>

                <p className="text-secondaryColor font-semibold text-sm mb-3">
                  {course.instituteName}
                </p>

                <p className="text-secondaryTextColor text-sm mb-4 line-clamp-3">
                  {course.description}
                </p>

                <div>
                  {course.programs && course.programs.length > 0 ? (
                    course.programs.map((program, index) => (
                      <div
                        key={index}
                        className="flex flex-wrap gap-2 mb-2 last:mb-0"
                      >
                        <span className="bg-gray-100 text-primaryTextColor text-xs px-3 py-1 rounded-full">
                          {program.programType || "Program Type N/A"}
                        </span>
                        <span className="bg-gray-100 text-primaryTextColor text-xs px-3 py-1 rounded-full">
                          {program.duration || "Duration N/A"}
                        </span>
                        <span className="bg-gray-100 text-primaryTextColor text-xs px-3 py-1 rounded-full">
                          {program.credits
                            ? `${program.credits} Credits`
                            : "No Credits"}
                        </span>
                      </div>
                    ))
                  ) : (
                    // Fallback
                    <div className="flex flex-wrap gap-2">
                      <span className="bg-gray-100 text-primaryTextColor text-xs px-3 py-1 rounded-full">
                        {course.programType || "Program Type N/A"}
                      </span>
                      <span className="bg-gray-100 text-primaryTextColor text-xs px-3 py-1 rounded-full">
                        {course.duration || "Duration N/A"}
                      </span>
                      <span className="bg-gray-100 text-primaryTextColor text-xs px-3 py-1 rounded-full">
                        {course.credits
                          ? `${course.credits} Credits`
                          : "No Credits"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
};

export default ExploreCourses;
